import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Tooltip, Switch, IconButton, Box, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { motion, AnimatePresence } from "framer-motion";
import { toggleFunMode } from "../redux/fun-mode/funModeSlice";

const FunModeToggleButton = () => {
  const isFunMode = useSelector((state) => state.funMode.isFunMode);
  const dispatch = useDispatch();

  const handleToggle = () => {
    dispatch(toggleFunMode());
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      style={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        position: "relative",
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          borderRadius: "50px",
          padding: "8px 24px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          width: "fit-content",
          mx: "auto",
          mt: 2,
          position: "relative",
          overflow: "hidden",
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: isFunMode 
              ? "linear-gradient(90deg, #ff3366, #ffcc00)" 
              : "linear-gradient(90deg, #00ff88, #0099ff)",
          },
          '&:hover': {
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.4)",
            transform: "translateY(-2px)",
          },
          transition: "all 0.3s ease",
        }}
      >
        {/* Animated Background */}
        <motion.div
          animate={{
            background: isFunMode
              ? [
                  "linear-gradient(45deg, rgba(255, 51, 102, 0.1), rgba(255, 204, 0, 0.1))",
                  "linear-gradient(45deg, rgba(255, 204, 0, 0.1), rgba(255, 51, 102, 0.1))"
                ]
              : [
                  "linear-gradient(45deg, rgba(0, 255, 136, 0.1), rgba(0, 153, 255, 0.1))",
                  "linear-gradient(45deg, rgba(0, 153, 255, 0.1), rgba(0, 255, 136, 0.1))"
                ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
          }}
        />

        {/* Switch */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Switch
            checked={isFunMode}
            onChange={handleToggle}
            sx={{
              "& .MuiSwitch-switchBase": {
                "&.Mui-checked": {
                  color: "#ffcc00",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#ff3366",
                  },
                },
              },
              "& .MuiSwitch-thumb": {
                backgroundColor: isFunMode ? "#ffcc00" : "#00ff88",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
              },
              "& .MuiSwitch-track": {
                backgroundColor: isFunMode ? "#ff3366" : "#0099ff",
                opacity: 0.8,
              },
            }}
          />
        </motion.div>

        {/* Text Content */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <motion.div
            animate={{ 
              scale: isFunMode ? [1, 1.2, 1] : 1,
              rotate: isFunMode ? [0, 5, -5, 0] : 0
            }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              sx={{
                fontSize: "1.5rem",
                filter: isFunMode ? "drop-shadow(0 0 8px rgba(255, 204, 0, 0.5))" : "none",
              }}
            >
              {isFunMode ? "🎭" : "😴"}
            </Typography>
          </motion.div>

          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: "14px",
              background: isFunMode
                ? "linear-gradient(45deg, #ffcc00, #ff3366)"
                : "linear-gradient(45deg, #00ff88, #0099ff)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            Fun Mode
            <AnimatePresence mode="wait">
              <motion.span
                key={isFunMode ? "on" : "off"}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {isFunMode ? "ACTIVE 🔥" : "INACTIVE"}
              </motion.span>
            </AnimatePresence>
          </Typography>
        </Box>

        {/* Info Tooltip */}
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Tooltip
            title={
              <Box sx={{ maxWidth: 280, p: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#00ff88", mb: 1 }}>
                  🎉 Fun Mode Activated!
                </Typography>
                <Typography variant="body2" sx={{ color: "white", mb: 1 }}>
                  Adds humor and entertainment to your experience! Transform boring interactions into fun conversations.
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0, color: "rgba(255,255,255,0.8)" }}>
                  <li>
                    <Typography variant="caption">
                      If something seems inappropriate, email us at{" "}
                      <strong style={{ color: "#00ff88" }}>services@findmyroommates.in</strong>
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="caption">
                      Switch back to <strong>Normal Mode</strong> anytime
                    </Typography>
                  </li>
                </Box>
              </Box>
            }
            componentsProps={{
              tooltip: {
                sx: {
                  background: "linear-gradient(135deg, rgba(0, 29, 61, 0.95) 0%, rgba(0, 8, 20, 0.95) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "12px",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
                },
              },
            }}
            arrow
          >
            <IconButton
              size="small"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                '&:hover': {
                  color: "#00ff88",
                  background: "rgba(0, 255, 136, 0.1)",
                  border: "1px solid rgba(0, 255, 136, 0.3)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </motion.div>

        {/* Active State Particles */}
        <AnimatePresence>
          {isFunMode && (
            <>
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                    x: [0, (i - 1) * 20],
                    y: [0, -30]
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.3,
                    ease: "easeOut"
                  }}
                  style={{
                    position: "absolute",
                    fontSize: "8px",
                    color: ["#ff3366", "#ffcc00", "#00ff88"][i],
                  }}
                >
                  {["✨", "🎉", "🔥"][i]}
                </motion.div>
              ))}
            </>
          )}
        </AnimatePresence>
      </Box>
    </motion.div>
  );
};

export default FunModeToggleButton;